import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from '@design-system/box';
import { CATEGORIES, FontAwesomeIcon } from '@design-system/font-awesome-icon';
import Color from 'color';
import { toNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Hue, Saturation } from 'react-color/lib/components/common';
import { styled } from 'styled-components';
import { getColorObject, getCSSGradient, getInputColor } from './utils';
const MODES = Object.freeze({
    CLICK: 'CLICK',
    INPUT: 'INPUT',
});
export const GradientPicker = ({ Input, defaultGradient, displayPreview = false, gradient, missingDataMessage, onChange, }) => {
    const initialGradient = useMemo(() => gradient, []);
    const [mode, setMode] = useState(MODES.INPUT);
    const [selectedColorPicker, setSelectedColorPicker] = useState(1);
    const [color1Percentage, setColor1Percentage] = useState(initialGradient.color1Percentage);
    const [color2Percentage, setColor2Percentage] = useState(initialGradient.color2Percentage);
    const [angle, setAngle] = useState(initialGradient.angle);
    const [selectedColor1, setSelectedColor1] = useState(getInputColor({
        color: initialGradient.color1,
        defaultColor: defaultGradient.color1,
    }));
    const color1 = useMemo(() => getColorObject({ color: selectedColor1 }).alpha(1), [selectedColor1]);
    const handleInputColor1Change = (value) => {
        setSelectedColor1(value);
    };
    const handleInputColor1Blur = () => {
        if ((selectedColor1 === null || selectedColor1 === void 0 ? void 0 : selectedColor1.length) !== 6) {
            setSelectedColor1(getInputColor({ color: defaultGradient.color1 }));
        }
    };
    const [hsl1, setHsl1] = useState(color1.hsl().object());
    const [hsv1, setHsv1] = useState(color1.hsv().object());
    useEffect(() => {
        setHsl1(color1.hsl().object());
        setHsv1(color1.hsv().object());
    }, [color1]);
    useEffect(() => {
        if (mode === MODES.CLICK && hsv1.h !== hsl1.h) {
            const hslToHsv = { h: hsl1.h, s: hsv1.s, v: hsv1.v };
            setHsv1(hslToHsv);
            setSelectedColor1(getInputColor({
                color: Color.hsv(hslToHsv.h, hslToHsv.s, hslToHsv.v).hex(),
            }));
        }
    }, [hsv1.h, hsl1.h, mode, hsv1.s, hsv1.v]);
    useEffect(() => {
        if (mode === MODES.CLICK) {
            setSelectedColor1(getInputColor({ color: Color(hsv1).hex() }));
        }
    }, [hsv1, hsv1.s, hsv1.v, mode]);
    const [selectedColor2, setSelectedColor2] = useState(getInputColor({
        color: initialGradient.color2,
        defaultColor: defaultGradient.color2,
    }));
    const color2 = useMemo(() => getColorObject({ color: selectedColor2 }).alpha(1), [selectedColor2]);
    const handleInputColor2Change = (value) => {
        setSelectedColor2(value);
    };
    const handleInputColor2Blur = () => {
        if ((selectedColor2 === null || selectedColor2 === void 0 ? void 0 : selectedColor2.length) !== 6) {
            setSelectedColor2(getInputColor({ color: defaultGradient.color2 }));
        }
    };
    const [hsl2, setHsl2] = useState(color2.hsl().object());
    const [hsv2, setHsv2] = useState(color2.hsv().object());
    useEffect(() => {
        if (mode === MODES.INPUT) {
            setHsl2(color2.hsl().object());
            setHsv2(color2.hsv().object());
        }
    }, [color2, mode]);
    useEffect(() => {
        if (mode === MODES.CLICK && hsv2.h !== hsl2.h) {
            const hslToHsv = { h: hsl2.h, s: hsv2.s, v: hsv2.v };
            setHsv2(hslToHsv);
            setSelectedColor2(getInputColor({
                color: Color.hsv(hslToHsv.h, hslToHsv.s, hslToHsv.v).hex(),
            }));
        }
    }, [hsv2.h, hsl2.h, mode, hsv2.s, hsv2.v]);
    useEffect(() => {
        if (mode === MODES.CLICK) {
            setSelectedColor2(getInputColor({ color: Color(hsv2).hex() }));
        }
    }, [hsv2, hsv2.s, hsv2.v, mode]);
    const inlineCSS = getCSSGradient({
        gradient: {
            angle,
            color1: `#${selectedColor1}`,
            color1Percentage,
            color2: `#${selectedColor2}`,
            color2Percentage,
            type: 'linear',
        },
    });
    const calculatedGradient = {
        angle,
        color1: `#${selectedColor1}`,
        color1Percentage: Number.isNaN(toNumber(color1Percentage))
            ? 0
            : toNumber(color1Percentage),
        color2: `#${selectedColor2}`,
        color2Percentage: Number.isNaN(toNumber(color2Percentage))
            ? 100
            : toNumber(color2Percentage),
        inlineCSS,
        type: 'linear',
    };
    useEffect(() => {
        onChange(calculatedGradient);
    }, [calculatedGradient, onChange]);
    return (_jsxs(GradientPickerWrapper, { children: [_jsx(ColorToolWrapper, { height: displayPreview ? 75 : 150, children: _jsx(Saturation, { hsl: selectedColorPicker === 1 ? hsl1 : hsl2, hsv: {
                        h: selectedColorPicker === 1 ? hsv1.h : hsv2.h,
                        s: selectedColorPicker === 1 ? hsv1.s / 100 : hsv2.s / 100,
                        v: selectedColorPicker === 1 ? hsv1.v / 100 : hsv2.v / 100,
                    }, onChange: (value) => {
                        const newHsv = {
                            h: value.h,
                            s: value.s * 100,
                            v: value.v * 100,
                        };
                        setMode(MODES.CLICK);
                        if (selectedColorPicker === 1) {
                            setHsv1(newHsv);
                        }
                        else {
                            setHsv2(newHsv);
                        }
                    } }) }), _jsx(ColorToolWrapper, { height: 10, children: _jsx(Hue, { hsl: selectedColorPicker === 1 ? hsl1 : hsl2, onChange: (value) => {
                        const newHsl = {
                            h: value.h,
                            l: value.l,
                            s: value.s,
                        };
                        setMode(MODES.CLICK);
                        if (selectedColorPicker === 1) {
                            setHsl1(newHsl);
                        }
                        else {
                            setHsl2(newHsl);
                        }
                    } }) }), _jsxs(Preview, { children: [_jsx(ColorPreview, { bg: color1.rgb().string(), onClick: () => {
                            setSelectedColorPicker(1);
                        } }), _jsxs(InputWrapper, { children: [_jsxs(Box, { alignItems: "center", mr: 3, width: "60%", children: [_jsx(Box, { mr: 1, children: "#" }), _jsx(Input, { ariaLabel: "Gradient color 1", dataTestId: "color-input-1", name: "color-input-1", onBlur: handleInputColor1Blur, onChange: (e) => {
                                            handleInputColor1Change(e.target.value);
                                        }, onFocus: () => {
                                            setMode(MODES.INPUT);
                                            setSelectedColorPicker(1);
                                        }, value: selectedColor1, variant: "small" })] }), _jsxs(Box, { alignItems: "center", width: "40%", children: [_jsx(Input, { ariaLabel: "Color 1 intensity", dataTestId: "percentage-input-1", name: "percentage-input-1", onBlur: (e) => {
                                            if (!e.target.value || Number.isNaN(toNumber(e.target.value))) {
                                                setColor1Percentage(0);
                                            }
                                        }, onChange: (e) => {
                                            setColor1Percentage(e.target.value);
                                        }, type: "number", value: color1Percentage, variant: "small" }), _jsx(Box, { ml: 1, children: "%" })] })] })] }), _jsxs(Preview, { children: [_jsx(ColorPreview, { bg: color2.rgb().string(), onClick: () => {
                            setSelectedColorPicker(2);
                        } }), _jsxs(InputWrapper, { children: [_jsxs(Box, { alignItems: "center", mr: 3, width: "60%", children: [_jsx(Box, { mr: 1, children: "#" }), _jsx(Input, { ariaLabel: "Gradient color 2", dataTestId: "color-input-2", name: "color-input-2", onBlur: handleInputColor2Blur, onChange: (e) => {
                                            handleInputColor2Change(e.target.value);
                                        }, onFocus: () => {
                                            setMode(MODES.INPUT);
                                            setSelectedColorPicker(2);
                                        }, value: selectedColor2, variant: "small" })] }), _jsxs(Box, { alignItems: "center", width: "40%", children: [_jsx(Input, { ariaLabel: "Color 1 intensity", dataTestId: "percentage-input-2", name: "percentage-input-2", onBlur: (e) => {
                                            if (!e.target.value || Number.isNaN(toNumber(e.target.value))) {
                                                setColor2Percentage(100);
                                            }
                                        }, onChange: (e) => {
                                            setColor2Percentage(e.target.value);
                                        }, type: "number", value: color2Percentage, variant: "small" }), _jsx(Box, { ml: 1, children: "%" })] })] })] }), _jsxs(Preview, { children: [_jsx(Box, { mr: 2, children: _jsx(FontAwesomeIcon, { height: "16px", iconName: "angle-90", iconType: CATEGORIES.LIGHT }) }), _jsx(Input, { ariaLabel: "Gradient angle", dataTestId: "angle-input", name: "angle-input", onBlur: (e) => {
                            if (!e.target.value) {
                                setAngle(0);
                            }
                        }, onChange: (e) => {
                            if (e.target.value === '') {
                                setAngle(e.target.value);
                                return;
                            }
                            setAngle(Number.parseInt(e.target.value, 10));
                        }, value: angle, variant: "small" })] }), displayPreview && (_jsxs(GradientPreviewWrapper, { children: [_jsx(GradientPreview, { "data-gradient": inlineCSS, "data-testid": "gradient-preview", gradient: inlineCSS }), _jsx(MissingDataWrapper, { children: missingDataMessage || 'Missing data for gradient' })] }))] }));
};
const GradientPickerWrapper = styled(Box).attrs(() => ({
    bg: 'white',
    flexDirection: 'column',
    p: 2,
    width: 220,
})) `
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;
const ColorToolWrapper = styled(Box).attrs(({ height }) => ({
    height,
    mb: 2,
})) `
  cursor: pointer;
  position: relative;
  user-select: none;
`;
const Preview = styled(Box).attrs(() => ({
    alignItems: 'center',
    mt: 2,
})) ``;
const ColorPreview = styled(Box).attrs(({ bg }) => ({
    bg,
    height: 24,
    mr: 3,
    width: 24,
})) `
  border-radius: 2px;
  cursor: pointer;
`;
const InputWrapper = styled(Box).attrs(() => ({
    alignItems: 'center',
    flex: 1,
})) ``;
const GradientPreviewWrapper = styled(Box).attrs(() => ({
    alignItems: 'center',
    height: 100,
    justifyContent: 'center',
    mt: 3,
    position: 'relative',
    width: '100%',
})) `
  border-radius: 2px;
`;
const GradientPreview = styled(Box)
    .withConfig({
    shouldForwardProp: (prop) => !['gradient'].includes(prop),
})
    .attrs(() => ({
    height: '100%',
    width: '100%',
})) `
    z-index: 1;
    border-radius: 2px;
    background-image: ${({ gradient }) => gradient};
  `;
const MissingDataWrapper = styled(Box).attrs(() => ({
    alignItems: 'center',
    bottom: 0,
    height: '100%',
    justifyContent: 'center',
    left: 0,
    p: 2,
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%',
})) `
  text-align: center;
`;
//# sourceMappingURL=GradientPicker.js.map